import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useParams,
  useLocation,
  type LoaderFunctionArgs,
} from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Sentry } from "@/lib/sentry";
import { PublicErrorFallback } from "@/components/website/PublicErrorFallback";
import { AdminErrorFallback } from "@/components/AdminErrorFallback";
import { initAnalytics, analyticsEnabled, trackPageView } from "@/lib/analytics";
import Dashboard from "./pages/Dashboard";
import MenuItemsPage from "./pages/MenuItemsPage";
import PackageEditorPage from "./pages/PackageEditorPage";
import QuotesPage from "./pages/QuotesPage";
import CatalogPage from "./pages/CatalogPage";
import PublicPackagePage from "./pages/PublicPackagePage";
import PublicPackagesBrowsePage from "./pages/PublicPackagesBrowsePage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import NotFound from "./pages/NotFound";
import PublicQuotePage from "./pages/PublicQuotePage";
import RootRedirect from "./pages/RootRedirect";
import MenuBuilderPage from "./pages/MenuBuilderPage";
import PublicMenuPage from "./pages/PublicMenuPage";
import FeatureFlagsPage from "./pages/FeatureFlagsPage";
import MyAccountPage from "./pages/MyAccountPage";
import ChangelogPage from "./pages/ChangelogPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import OrderRoute from "./pages/OrderRoute";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";
import EventsAdminPage from "./pages/EventsAdminPage";
import ContactsPage from "./pages/ContactsPage";
import AutomationsPage from "./pages/AutomationsPage";
import TestimonialsAdminPage from "./pages/admin/TestimonialsAdminPage";
import WhyChooseUsAdminPage from "./pages/admin/WhyChooseUsAdminPage";
import WebsiteContentSettingsPage from "./pages/admin/WebsiteContentSettingsPage";
import BrandingPage from "./pages/admin/BrandingPage";
// Lazy-load the Meta Ads admin module — it's feature-flag gated, admin-only,
// and pulls in its own sub-tree of components + hooks. Splitting it out keeps
// the main bundle under the 1600 kB gate.
const MetaAdsPage = lazy(() => import("./pages/admin/MetaAdsPage"));
// Live Ads — read-only Meta campaigns for staff (#747), lazy for the same reason.
const MetaAdsOverviewPage = lazy(() => import("./pages/admin/MetaAdsOverviewPage"));
// Google Ads — read-only reporting (#519), lazy for the same bundle reason.
const GoogleAdsPage = lazy(() => import("./pages/admin/GoogleAdsPage"));
// Social — read-only organic FB/IG posts + engagement (#975), lazy for the same reason.
const SocialPage = lazy(() => import("./pages/admin/SocialPage"));
// Studio — super_admin-only media store (#498). Lazy for the same reason:
// it pulls in its own dialog + hooks and nobody but Frank ever opens it.
const StudioPage = lazy(() => import("./pages/admin/StudioPage"));
// One event (#1429): lazy, it pulls in the quote panel, tabs and message thread.
const EventPage = lazy(() => import("./pages/EventPage"));

function LazyRouteFallback() {
  return (
    <div className="flex h-[60vh] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}
import StaffPage from "./pages/admin/StaffPage";
import MarketingPagesAdminPage from "./pages/admin/MarketingPagesAdminPage";
import ServicesAdminPage from "./pages/admin/ServicesAdminPage";
import RoadmapPage from "./pages/admin/RoadmapPage";
import PlanWizardPage from "./pages/PlanWizardPage";
import { FeedbackWidget } from "./components/FeedbackWidget";
import { InstallPrompt } from "./components/pwa/InstallPrompt";
import { RequestButton } from "./components/requests/RequestButton";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import { MARKETING_DOMAIN } from "./lib/constants";
import { useAppSettings } from "./hooks/useAppSettings";
import {
  publicPackageQuery,
  publishedPackagesQuery,
} from "./hooks/usePublicPackage";
import { publicMenuQuery } from "./hooks/usePublishedMenus";

/** Redirects old CMS slugs (e.g. /about, /contact) to the marketing site */
function CmsRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const { data: settings, isLoading } = useAppSettings();
  useEffect(() => {
    // Wait for settings to load so we don't redirect to the fallback domain
    // when a configured one exists.
    if (isLoading) return;
    const marketingDomain = settings?.marketing_domain_url || MARKETING_DOMAIN;
    const safePath = (slug ?? "").replace(/^[/\\@]+/, "");
    window.location.replace(`${marketingDomain}/${safePath}`);
  }, [isLoading, settings, slug]);
  return null;
}

// Conservative defaults to cut refetch storms. Most admin data doesn't change
// while a user is tabbed away, and React Query's defaults (staleTime: 0 +
// refetchOnWindowFocus: true) cause a full reload every time focus returns.
// Individual hooks can override for data that truly needs to be live.
//
// refetchOnMount: "stale" (rather than "always") lets SPA nav back to a
// page read from cache when the data is still within staleTime (issue #146).
// Hooks that need live data (useQuotes, useFilteredQuotes) opt into
// "always" locally.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      // `true` already means "refetch on mount only when stale" (governed by
      // staleTime above). The prior "stale" string was not a valid React Query
      // value and silently fell back to the default.
      refetchOnMount: true,
      retry: 1,
    },
  },
});

/**
 * Root layout — provides AuthProvider + chrome (FeedbackWidget) for every
 * route. `<Outlet />` renders the matched route's element.
 *
 * Everything that needs router context (useNavigate, useLocation, Link)
 * must live inside the router, which means inside this layout.
 */
function RootLayout() {
  const qc = useQueryClient();
  // app_settings' anon RLS policy is key-filtered (#681), so the row set the
  // shared useAppSettings hook returns depends on whether the caller is signed
  // in — but its cache key is a constant and staleTime is 5 minutes. Without
  // this, a visitor who browses a public page and then signs in keeps the
  // anon-only map, and an admin screen reading e.g. `automation_enabled` off it
  // renders the wrong state. This lives here, not in the hook: RootLayout never
  // unmounts, whereas every useAppSettings observer does on nav to /login.
  // TOKEN_REFRESHED is excluded on purpose — it fires on a timer.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        // Deferred, matching AuthContext.tsx:64 — supabase-js holds a lock
        // across this callback and warns against calling back into it from
        // here. Invalidating is not itself a Supabase call, but on SIGNED_OUT
        // from an admin page the query IS active, so React Query would refetch
        // inside the lock. The 0ms hop takes that off the table.
        setTimeout(() => qc.invalidateQueries({ queryKey: ["app_settings"] }), 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [qc]);

  return (
    <AuthProvider>
      <FeedbackWidget />
      <InstallPrompt />
      <RequestButton />
      <Outlet />
    </AuthProvider>
  );
}

// ─── Analytics (issue #203) ────────────────────────────────────────────────
// Pixel/GA4 load lazily on the first navigation to a NON-skip-listed public
// path, and PageView fires only for those paths. Scripts never load on admin
// (different layout), and never on these public routes that carry tokens/PII or
// are pure redirects:
//   - /quote/*        capability URLs (anyone with the UUID can view)
//   - /login          no value, and a script-load surface for users en route
//   - /reset-password recovery/invite tokens live in the URL hash (`dl` leak)
//   - /accept-invite  invite token_hash lives in the query string (`dl` leak)
//   - /               RootRedirect bounce + Supabase error-fragment redirects
// Matched on a trailing-slash-normalized pathname (also the dedupe key).
const SKIP_TRACKING_PATTERNS: RegExp[] = [
  /^\/quote(\/|$)/,
  /^\/login$/,
  /^\/reset-password$/,
  /^\/accept-invite$/,
  /^\/$/,
];

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

/** Sanitize a path for reporting so no UUIDs/tokens reach Meta/GA. */
function sanitizeTrackedPath(pathname: string): string {
  return pathname.replace(/^\/quote\/[^/]+/, "/quote/:id");
}

// Module-level so it survives the layout unmount/remount when crossing the
// admin<->public boundary, preventing a duplicate page_view on return.
let lastTrackedPath: string | null = null;

/**
 * Fires a single PageView/page_view per public navigation and lazily boots the
 * trackers on first eligible path. Mounted inside PublicLayoutRoute only.
 */
function AnalyticsListener() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!analyticsEnabled()) return;
    const normalized = normalizePath(pathname);
    if (SKIP_TRACKING_PATTERNS.some((re) => re.test(normalized))) return;
    if (normalized === lastTrackedPath) return;
    lastTrackedPath = normalized;
    initAnalytics();
    trackPageView(sanitizeTrackedPath(normalized));
  }, [pathname]);
  return null;
}

/**
 * Public-only layout — wraps public routes in a Sentry ErrorBoundary tagged
 * `boundary: public` so a crash on /packages never takes down admin.
 */
function PublicLayoutRoute() {
  return (
    <Sentry.ErrorBoundary
      fallback={<PublicErrorFallback />}
      showDialog={false}
      beforeCapture={(scope) => {
        scope.setTag("boundary", "public");
      }}
    >
      <AnalyticsListener />
      <CookieConsentBanner />
      <Outlet />
    </Sentry.ErrorBoundary>
  );
}

/**
 * Admin-only layout — separate ErrorBoundary so one crash in a public page
 * doesn't brick admin (and vice versa).
 */
function AdminLayoutRoute() {
  return (
    <Sentry.ErrorBoundary
      fallback={<AdminErrorFallback />}
      showDialog={false}
      beforeCapture={(scope) => {
        scope.setTag("boundary", "admin");
      }}
    >
      <Outlet />
    </Sentry.ErrorBoundary>
  );
}

// ─── Route loaders (issue #147) ──────────────────────────────────────────
// Kick off data fetches in parallel with component code loading. We use
// `prefetchQuery` (not `ensureQueryData`) so loader errors don't bubble to
// the router's errorElement — the component's own hook observes the same
// error state and renders the service-down UI with a retry button (#144).

function publicPackagesLoader() {
  queryClient.prefetchQuery(publishedPackagesQuery());
  return null;
}

function publicPackageLoader({ params }: LoaderFunctionArgs) {
  const slug = params.slug;
  if (slug) queryClient.prefetchQuery(publicPackageQuery(slug));
  return null;
}

function publicMenuLoader({ params }: LoaderFunctionArgs) {
  const slug = params.slug;
  if (slug) queryClient.prefetchQuery(publicMenuQuery(slug));
  return null;
}

// ─── Router ──────────────────────────────────────────────────────────────

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public routes — wrapped in the public error boundary
      {
        element: <PublicLayoutRoute />,
        children: [
          { path: "/", element: <RootRedirect /> },
          { path: "/site/*", element: <CmsRedirect /> },
          { path: "/login", element: <LoginPage /> },
          { path: "/reset-password", element: <ResetPasswordPage /> },
          { path: "/accept-invite", element: <AcceptInvitePage /> },
          { path: "/quote/:id", element: <PublicQuotePage /> },
          {
            path: "/menu/:slug",
            loader: publicMenuLoader,
            element: <PublicMenuPage />,
          },
          {
            path: "/packages",
            loader: publicPackagesLoader,
            element: <PublicPackagesBrowsePage />,
          },
          {
            path: "/packages/:slug",
            loader: publicPackageLoader,
            element: <PublicPackagePage />,
          },
          { path: "/order", element: <OrderRoute /> },
          { path: "/plan", element: <PlanWizardPage /> },
          { path: "/privacy", element: <PrivacyPage /> },
          { path: "/terms", element: <TermsPage /> },
          { path: "/cookies", element: <CookiesPage /> },
          // Redirect old CMS slugs to marketing site — must come after the
          // explicit public routes above. `*` catches everything else.
          { path: "/:slug", element: <CmsRedirect /> },
          { path: "*", element: <NotFound /> },
        ],
      },
      // Admin routes — wrapped in the admin error boundary
      {
        element: <AdminLayoutRoute />,
        children: [
          {
            path: "/admin",
            element: (
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/account",
            element: (
              <ProtectedRoute>
                <MyAccountPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/menu-items",
            element: (
              <ProtectedRoute requiredRole="staff">
                <MenuItemsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/catalog",
            element: (
              <ProtectedRoute requiredRole="staff">
                <CatalogPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/automations",
            element: (
              <ProtectedRoute requiredRole="staff">
                <AutomationsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/packages",
            element: <Navigate to="/admin/catalog?tab=packages" replace />,
          },
          {
            path: "/admin/packages/new",
            element: (
              <ProtectedRoute requiredRole="staff">
                <PackageEditorPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/packages/:id/edit",
            element: (
              <ProtectedRoute requiredRole="staff">
                <PackageEditorPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/quotes",
            element: (
              <ProtectedRoute requiredRole="staff">
                <QuotesPage />
              </ProtectedRoute>
            ),
          },
          // /admin/orders removed in #105; online orders land in /admin/quotes as source=online_order
          {
            path: "/admin/orders",
            element: <Navigate to="/admin/quotes" replace />,
          },
          {
            path: "/admin/events",
            element: (
              <ProtectedRoute requiredRole="staff">
                <EventsAdminPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/events/:id",
            element: (
              <ProtectedRoute requiredRole="staff">
                <Suspense fallback={<LazyRouteFallback />}>
                  <EventPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/clients",
            element: (
              <ProtectedRoute requiredRole="staff">
                <ContactsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/calendar",
            element: (
              <ProtectedRoute requiredRole="staff">
                <ComingSoonPage
                  title="Calendar"
                  description="View all your events, quotes, and bookings on a unified calendar."
                />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/ai-chef",
            element: (
              <ProtectedRoute requiredRole="staff">
                <ComingSoonPage
                  title="AI Chef"
                  description="Get AI-powered menu suggestions, cost optimization, and seasonal recommendations."
                />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/menus",
            element: <Navigate to="/admin/catalog?tab=menus" replace />,
          },
          {
            path: "/admin/menus/new",
            element: (
              <ProtectedRoute requiredRole="staff">
                <MenuBuilderPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/menus/:id/edit",
            element: (
              <ProtectedRoute requiredRole="staff">
                <MenuBuilderPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/team",
            element: (
              <ProtectedRoute requiredRole="admin">
                <UsersPage />
              </ProtectedRoute>
            ),
          },
          // #1467: the Users page has always been /admin/team, but /admin/users is the
          // obvious guess (the component is UsersPage). Unwrapped, like the other
          // redirects here; the target route does the auth gating.
          {
            path: "/admin/users",
            element: <Navigate to="/admin/team" replace />,
          },
          {
            path: "/admin/staff",
            element: (
              <ProtectedRoute requiredRole="staff">
                <StaffPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/settings",
            element: (
              <ProtectedRoute requiredRole="admin">
                <SettingsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/changelog",
            element: (
              <ProtectedRoute>
                <ChangelogPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/feature-flags",
            element: (
              <ProtectedRoute requiredRole="super_admin">
                <FeatureFlagsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/roadmap",
            element: (
              // requiredRole="staff" admits staff, admin (via ProtectedRoute's
              // adminCarveOut) and super_admin, and refuses viewer. Not SuperAdminRoute:
              // the roadmap is for the whole team.
              <ProtectedRoute requiredRole="staff">
                <RoadmapPage />
              </ProtectedRoute>
            ),
          },
          // /admin/requests: the Requests board is gone (#1568; requests are GitHub
          // issues now). Redirect kept so old bookmarks land somewhere useful.
          // Unwrapped, like the other redirects in this file — the target route does the
          // auth gating.
          {
            path: "/admin/requests",
            element: <Navigate to="/admin/roadmap" replace />,
          },
          {
            path: "/admin/website/testimonials",
            element: (
              <ProtectedRoute requiredRole="staff">
                <TestimonialsAdminPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/website/why-choose-us",
            element: (
              <ProtectedRoute requiredRole="staff">
                <WhyChooseUsAdminPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/website/content",
            element: (
              <ProtectedRoute requiredRole="staff">
                <WebsiteContentSettingsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/website/pages",
            element: (
              <ProtectedRoute requiredRole="admin">
                <MarketingPagesAdminPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/website/services",
            element: (
              <ProtectedRoute requiredRole="admin">
                <ServicesAdminPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "/admin/branding",
            element: (
              <ProtectedRoute requiredRole="staff">
                <BrandingPage />
              </ProtectedRoute>
            ),
          },
          // Meta Ads — admin-only, feature-flag gated
          {
            path: "/admin/ads",
            element: (
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<LazyRouteFallback />}>
                  <MetaAdsPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          // Live Ads — read-only Meta campaigns, staff and up, feature-flag gated (#747)
          {
            path: "/admin/live-ads",
            element: (
              <ProtectedRoute requiredRole="staff">
                <Suspense fallback={<LazyRouteFallback />}>
                  <MetaAdsOverviewPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          // Google Ads — read-only reporting, staff and up, feature-flag gated (#519)
          {
            path: "/admin/google-ads",
            element: (
              <ProtectedRoute requiredRole="staff">
                <Suspense fallback={<LazyRouteFallback />}>
                  <GoogleAdsPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          // Social — organic FB/IG posts, staff and up, feature-flag gated (#975)
          {
            path: "/admin/social",
            element: (
              <ProtectedRoute requiredRole="staff">
                <Suspense fallback={<LazyRouteFallback />}>
                  <SocialPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          // Studio — super_admin only, feature-flag gated (#498)
          {
            path: "/admin/studio",
            element: (
              <ProtectedRoute requiredRole="super_admin">
                <Suspense fallback={<LazyRouteFallback />}>
                  <StudioPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
]);

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <RouterProvider router={router} />
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
